import React from 'react';
import cx from 'classnames';
import { ChevronRight } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/ui/tooltip';
import { useIsMobile } from '@ui/hooks/use-mobile';
import { STATUS_META, TrendPoint } from './sources/types';

/**
 * Tiny dependency-free SVG/DOM charts for the client product dashboard; Insights reuses StackedBarChart.
 * Progress charts are deliberately neutral-coloured; colour is reserved for health status.
 */

export const Sparkline = ({
  data,
  stroke,
  fill,
  className,
}: {
  data: number[];
  stroke: string;
  fill?: string;
  className?: string;
}): JSX.Element => {
  const w = 100;
  const h = 36;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const points = data.map((d, i) => {
    const x = data.length > 1 ? (i / (data.length - 1)) * w : 0;
    const y = h - ((d - min) / range) * (h - 4) - 2;
    return [x, y] as const;
  });
  const line = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${line} L${w},${h} L0,${h} Z`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className={cx('h-10 w-full', className)}>
      {fill && <path d={area} style={{ fill }} opacity={0.15} />}
      <path
        d={line}
        style={{ fill: 'none', stroke }}
        strokeWidth={2}
        vectorEffect="non-scaling-stroke"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// "Nice" number helpers (Heckbert's algorithm) — turn a raw data range into round axis bounds/ticks.
const niceNum = (range: number, round: boolean): number => {
  const exp = Math.floor(Math.log10(range || 1));
  const frac = (range || 1) / 10 ** exp;
  // Thresholds differ for rounding (nearest) vs ceiling (fit above) the fraction to 1 / 2 / 5 / 10.
  const steps: [number, number][] = round
    ? [
        [1.5, 1],
        [3, 2],
        [7, 5],
      ]
    : [
        [1, 1],
        [2, 2],
        [5, 5],
      ];
  const match = steps.find(([limit]) => (round ? frac < limit : frac <= limit));
  const nice = match ? match[1] : 10;
  return nice * 10 ** exp;
};

const niceScale = (lo: number, hi: number, maxTicks = 4): { min: number; max: number; ticks: number[] } => {
  const step = niceNum(niceNum(hi - lo, false) / (maxTicks - 1), true);
  const min = Math.floor(lo / step) * step;
  const max = Math.ceil(hi / step) * step;
  const ticks: number[] = [];
  for (let t = min; t <= max + step / 2; t += step) ticks.push(Math.round(t));
  return { min, max, ticks };
};

// Catmull-Rom → cubic Bézier: a gently smoothed path through [x, y] points in the 0–100 plot space.
// Low tension keeps it faithful to the data (no big overshoots) while softening the polyline kinks.
const smoothPath = (pts: [number, number][], tension = 0.16): string => {
  if (pts.length < 2) return pts.length ? `M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)}` : '';
  const d = [`M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)}`];
  for (let i = 0; i < pts.length - 1; i += 1) {
    const p0 = pts[i - 1] ?? pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] ?? p2;
    const c1x = p1[0] + (p2[0] - p0[0]) * tension;
    const c1y = p1[1] + (p2[1] - p0[1]) * tension;
    const c2x = p2[0] - (p3[0] - p1[0]) * tension;
    const c2y = p2[1] - (p3[1] - p1[1]) * tension;
    d.push(
      `C${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${p2[0].toFixed(1)},${p2[1].toFixed(1)}`,
    );
  }
  return d.join(' ');
};

// Response time over time. A line graph with a value axis; health reads from a soft area fill under the
// line (in the current status colour) and thin dashed boundary lines where the app would tip into the
// next (worse) zone. The Y-scale fits the data (not a fixed 0–max) but always stretches far enough to
// reveal the NEXT boundary the app would degrade past — so even a healthy app shows how much headroom
// is left before the next zone. Points are hoverable for the exact value.
// How many x-axis labels a plot this width can show without them running together.
const MAX_X_LABELS = 7;
const MAX_X_LABELS_PHONE = 4;
// Counts back from the newest point, so "today" is always labelled whatever the series length.
const isXLabelled = (index: number, count: number, max = MAX_X_LABELS): boolean => {
  const stride = Math.max(1, Math.ceil(count / max));
  return index === 0 || index === count - 1 || (count - 1 - index) % stride === 0;
};

// Near-full width: a tiny inset keeps the first/last dots (with their ring) from spilling past the
// frame. The area fill still runs the full plot width (flat to each edge) — see below.
const EDGE_INSET = 1.5; // % — just enough to tuck the edge dots inside the frame

/** A health line over time: the frame, a banded fill, dashed boundaries and hoverable dots, on a scale the caller fits. */
const TrendPlot = ({
  series,
  ticks,
  min,
  y,
  formatValue,
  fillStops,
  boundaries,
  className,
}: {
  series: TrendPoint[];
  ticks: number[];
  min: number;
  y: (v: number) => number; // % from the top
  formatValue: (v: number) => string;
  fillStops: { offset: number; color: string; opacity: number }[]; // top to bottom, 0–100
  boundaries: { at: number; label: string; rgb: string }[];
  className?: string;
}): JSX.Element => {
  const isMobile = useIsMobile();
  const n = series.length;
  const uid = React.useId().replace(/:/g, '');
  const fillId = `respFill${uid}`;
  const fadeMaskId = `respFade${uid}`;
  const x = (i: number): number => (n > 1 ? EDGE_INSET + (i / (n - 1)) * (100 - 2 * EDGE_INSET) : 50);

  // The line is a neutral (grey) smoothed stroke; health colour lives in the banded area fill and the dots.
  // A gap day breaks it into runs, each drawn and filled on its own; the outer runs still reach the edges.
  const runs: { from: number; to: number; pts: [number, number][] }[] = [];
  series.forEach((p, i) => {
    if (p.gap) return;
    const last = runs[runs.length - 1];
    if (last && last.to === i - 1) {
      last.to = i;
      last.pts.push([x(i), y(p.value)]);
    } else runs.push({ from: i, to: i, pts: [[x(i), y(p.value)]] });
  });
  const lines = runs.filter(run => run.pts.length > 1).map(run => smoothPath(run.pts));
  const areas = runs
    .filter(run => run.pts.length > 1)
    .map(run => {
      const start = run.from === 0 ? 0 : run.pts[0][0];
      const end = run.to === n - 1 ? 100 : run.pts[run.pts.length - 1][0];
      const first = run.pts[0][1].toFixed(1);
      const last = run.pts[run.pts.length - 1][1].toFixed(1);
      return `M${start},100 L${start},${first} ${smoothPath(run.pts).replace(/^M/, 'L')} L${end},${last} L${end},100 Z`;
    });

  return (
    <div className={cx('flex flex-col', className)}>
      <div className="flex min-h-[9rem] flex-1">
        {/* Y-axis value labels — skip the bottom-most tick so it doesn't crowd the x-axis corner. */}
        <div className="relative mr-1.5 h-full w-9 shrink-0">
          {ticks
            .filter(t => t !== min)
            .map(t => (
              <span
                key={t}
                className="absolute right-0 -translate-y-1/2 text-[10px] leading-none text-muted-foreground tabular-nums"
                style={{ top: `${y(t)}%` }}
              >
                {formatValue(t)}
              </span>
            ))}
        </div>

        {/* Plot — a subtle L-frame (left + bottom, no arrows) grounds the gridlines and labels without
            the "math axis" implication that the scale runs to a 0 origin. */}
        <div className="relative h-full flex-1 border-b border-l border-border">
          {ticks.map(t => (
            <div key={t} className="absolute inset-x-0 border-t border-border/60" style={{ top: `${y(t)}%` }} />
          ))}
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            className="absolute inset-0 h-full w-full text-[#5b6b81]"
          >
            <defs>
              <linearGradient id={fillId} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2="100">
                {fillStops.map((stop, i) => (
                  // eslint-disable-next-line react/no-array-index-key -- the stops are positional
                  <stop key={i} offset={`${stop.offset}%`} stopColor={stop.color} stopOpacity={stop.opacity} />
                ))}
              </linearGradient>
              {/* Horizontal fade so the fill eases out past the edge points instead of ending on a hard
                  vertical line; it bottoms out at a partial grey so a little colour still shows at the edges. */}
              <linearGradient id={`${fadeMaskId}grad`} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="100" y2="0">
                <stop offset="0%" stopColor="#808080" />
                <stop offset={`${EDGE_INSET}%`} stopColor="#fff" />
                <stop offset={`${100 - EDGE_INSET}%`} stopColor="#fff" />
                <stop offset="100%" stopColor="#808080" />
              </linearGradient>
              <mask id={fadeMaskId}>
                <rect x="0" y="0" width="100" height="100" fill={`url(#${fadeMaskId}grad)`} />
              </mask>
            </defs>
            {areas.map(area => (
              <path key={area} d={area} mask={`url(#${fadeMaskId})`} style={{ fill: `url(#${fillId})` }} />
            ))}
            {lines.map(line => (
              <path
                key={line}
                d={line}
                style={{ fill: 'none', stroke: 'currentColor' }}
                strokeWidth={2}
                vectorEffect="non-scaling-stroke"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            ))}
          </svg>
          {/* Dashed zone boundaries — where the app would tip into the next (worse) zone. */}
          {boundaries.map(b => (
            <div
              key={b.label}
              className="absolute inset-x-0 -translate-y-1/2"
              style={{
                top: `${y(b.at)}%`,
                height: '1.5px',
                backgroundImage: `repeating-linear-gradient(to right, rgba(${b.rgb}, 0.6) 0, rgba(${b.rgb}, 0.6) 4px, transparent 4px, transparent 8px)`,
              }}
            />
          ))}
          {/* Points — coloured by status, hover for the exact period + value. */}
          {series.map((p, i) =>
            p.gap ? null : (
              // eslint-disable-next-line react/no-array-index-key -- order follows the series, which is stable
              <Tooltip key={i}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={`${p.label}: ${p.display}`}
                    className="absolute -translate-x-1/2 -translate-y-1/2 p-1.5"
                    style={{ left: `${x(i)}%`, top: `${y(p.value)}%` }}
                  >
                    <span
                      className="block size-2 rounded-full ring-2 ring-[#5b6b81] max-md:size-1 max-md:ring-0"
                      style={{ background: STATUS_META[p.status].stroke }}
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {p.label} · {p.display}
                </TooltipContent>
              </Tooltip>
            ),
          )}
        </div>
      </div>

      {/* Period labels, aligned to their x position. Only a subset is drawn: a daily series spans ~90
          points, and one label each would overlap into an unreadable smear. Every point keeps its label
          in the hover tooltip. */}
      <div className="mt-1 pl-[42px]">
        <div className="relative h-3 text-[10px] leading-none text-muted-foreground">
          {series.map((p, i) =>
            isXLabelled(i, n, isMobile ? MAX_X_LABELS_PHONE : MAX_X_LABELS) ? (
              <span
                // eslint-disable-next-line react/no-array-index-key -- order follows the series, which is stable
                key={i}
                // The newest label ends at its point rather than centring on it, so it stays inside the frame.
                className={cx('absolute whitespace-nowrap', i === n - 1 ? '-translate-x-full' : '-translate-x-1/2')}
                style={{ left: `${x(i)}%` }}
              >
                {p.label}
              </span>
            ) : null,
          )}
        </div>
      </div>
    </div>
  );
};

const GREEN = '#10b981';
const AMBER = '#f59e0b';
const RED = '#ef4444';

export const ResponseTrendChart = ({
  series,
  fastBelow,
  slowAtOrAbove,
  formatValue,
  className,
}: {
  series: TrendPoint[];
  fastBelow: number; // Fast → Moderate boundary (ms)
  slowAtOrAbove: number; // Moderate → Slow boundary (ms)
  formatValue: (v: number) => string; // formats a Y-axis tick, e.g. 500 → "0.5s"
  className?: string;
}): JSX.Element => {
  const values = series.map(p => p.value);
  const rawLo = Math.min(...values);
  const rawHi = Math.max(...values);
  const thresholds = [fastBelow, slowAtOrAbove];
  const upper = thresholds.find(b => b > rawHi + 1e-6); // next (worse) zone boundary above the data
  const lower = [...thresholds].reverse().find(b => b < rawLo - 1e-6); // nearest boundary below the data

  // Fit the data, but always reach past a neighbouring threshold so ≥2 zones are visible: normally the
  // worse zone above (headroom toward degrading); if already in the worst zone, the better one below.
  let lo: number;
  let hi: number;
  if (upper !== undefined) {
    const reach = upper - rawLo || upper * 0.2;
    lo = rawLo - reach * 0.5; // generous headroom below so the line isn't glued to the floor
    hi = upper + reach * 0.3;
  } else {
    // A spike past the slow line can sit beside days below every threshold, so the floor must still fit them.
    const floor = Math.min(lower ?? slowAtOrAbove, rawLo);
    const reach = rawHi - floor || rawHi * 0.2;
    lo = floor - reach * 0.3;
    hi = rawHi + reach * 0.15;
  }
  const { min, max } = niceScale(Math.max(0, lo), hi);
  const span = max - min || 1;
  // Gridlines/labels at a finer step than the bound calc, so the plot isn't left with just 2–3 lines.
  const tickStep = niceNum(span / 5, true);
  const ticks: number[] = [];
  for (let t = min; t <= max + tickStep / 2; t += tickStep) ticks.push(Math.round(t));
  const y = (v: number): number => (1 - (v - min) / span) * 100;

  // Dashed markers only for boundaries ABOVE the data: ones the line has crossed show in the fill's colour.
  const boundaries = [
    { at: fastBelow, label: 'Moderate', rgb: '245, 158, 11' }, // amber-500
    { at: slowAtOrAbove, label: 'Slow', rgb: '239, 68, 68' }, // red-500
  ].filter(b => b.at < max && b.at > rawHi);

  // The fill is banded by zone (red above Slow, amber to Fast, green below), keyed to each threshold's height.
  const clampY = (v: number): number => Math.min(100, Math.max(0, y(v)));
  const slowY = clampY(slowAtOrAbove);
  const fastY = clampY(fastBelow);
  const fillStops = [
    { offset: 0, color: RED, opacity: 0.14 },
    { offset: slowY, color: RED, opacity: 0.14 },
    { offset: slowY, color: AMBER, opacity: 0.14 },
    { offset: fastY, color: AMBER, opacity: 0.14 },
    { offset: fastY, color: GREEN, opacity: 0.16 },
    { offset: 100, color: GREEN, opacity: 0.16 },
  ];

  return (
    <TrendPlot
      series={series}
      ticks={ticks}
      min={min}
      y={y}
      formatValue={formatValue}
      fillStops={fillStops}
      boundaries={boundaries}
      className={className}
    />
  );
};

/**
 * Crash-free sessions over time, where higher is better: green at or above the threshold, amber below it.
 * The scale tops out at 100% and always reaches down past the threshold, so its dashed line is on the chart.
 */
export const CrashFreeTrendChart = ({
  series,
  greenAtOrAbove,
  className,
}: {
  series: TrendPoint[];
  greenAtOrAbove: number; // %
  className?: string;
}): JSX.Element => {
  const rawLo = Math.min(...series.filter(p => !p.gap).map(p => p.value), greenAtOrAbove);
  const reach = 100 - rawLo || 0.5;
  const { min } = niceScale(Math.max(0, rawLo - reach * 0.5), 100);
  const max = 100;
  const span = max - min || 1;
  const step = niceNum(span / 4, true);
  // Rounded to the step's own precision, so 0.1 steps do not print as 99.60000000000001.
  const digits = Math.max(0, -Math.floor(Math.log10(step)));
  const ticks: number[] = [];
  for (let t = min; t <= max + step / 2; t += step) ticks.push(Number(t.toFixed(digits)));
  const y = (v: number): number => (1 - (v - min) / span) * 100;
  const thresholdY = Math.min(100, Math.max(0, y(greenAtOrAbove)));

  return (
    <TrendPlot
      series={series}
      ticks={ticks}
      min={min}
      y={y}
      formatValue={v => `${v.toFixed(digits)}%`}
      fillStops={[
        { offset: 0, color: GREEN, opacity: 0.16 },
        { offset: thresholdY, color: GREEN, opacity: 0.16 },
        { offset: thresholdY, color: AMBER, opacity: 0.14 },
        { offset: 100, color: AMBER, opacity: 0.14 },
      ]}
      boundaries={[{ at: greenAtOrAbove, label: 'Threshold', rgb: '245, 158, 11' }]}
      className={className}
    />
  );
};

export interface StackedSeries {
  key: string; // stable identity — colour follows this, not the draw order
  label: string;
  color: string; // CSS colour (e.g. a var(--chapter-n)); the caller owns the colour→entity mapping
  data: number[]; // one value per category (bar), aligned to `labels`
}

// Vertical stacked bars: each bar is one category (a sprint), split into coloured segments (chapters).
// Segments carry a 2px surface gap; the topmost segment rounds (the stack's data-end). Each segment has
// a hover tooltip. Every series is kept mounted and positioned absolutely, so toggling a chapter or
// switching the value set animates smoothly: hidden segments collapse to zero height + fade out, and the
// segments above slide down as the bar re-scales. `hiddenKeys` drives which series are collapsed.
/**
 * The smallest round step (1, 2 or 5 times a power of ten) that reaches the top in three intervals or
 * fewer, so the axis reads 0, 2k, 4k, 6k rather than halves of whatever the tallest bar was. Whole
 * numbers only, and a top of at least 2, so a chart with nothing in it still has three distinct ticks.
 */
const axisStep = (rawMax: number): number => {
  const top = Math.max(rawMax, 2);
  for (let exp = Math.max(0, Math.floor(Math.log10(top / 3))); ; exp += 1) {
    const step = [1, 2, 5].map(m => m * 10 ** exp).find(each => Math.ceil(top / each) <= 3);
    if (step) return step;
  }
};

export const StackedBarChart = ({
  series,
  labels,
  tip,
  hiddenKeys,
  formatTick = String,
  heightClass = 'h-32',
  className,
}: {
  series: StackedSeries[]; // full set, bottom → top draw order (not pre-filtered)
  labels: string[];
  tip: (seriesIndex: number, barIndex: number, value: number) => string;
  hiddenKeys?: ReadonlySet<string>; // series to collapse; everything else is shown
  formatTick?: (value: number) => string; // y-axis tick formatter (default: plain number)
  heightClass?: string;
  className?: string;
}): JSX.Element => {
  const isHidden = (key: string): boolean => hiddenKeys?.has(key) ?? false;
  const valueAt = (s: StackedSeries, barIndex: number): number => (isHidden(s.key) ? 0 : (s.data[barIndex] ?? 0));
  const totals = labels.map((_, barIndex) => series.reduce((sum, s) => sum + valueAt(s, barIndex), 0));
  const rawMax = Math.max(...totals, 0) || 1;
  const step = axisStep(rawMax);
  const niceMax = Math.ceil(Math.max(rawMax, 2) / step) * step;
  const ticks = Array.from({ length: Math.round(niceMax / step) + 1 }, (_, i) => niceMax - i * step);

  return (
    <div className={cx('flex flex-col', className)}>
      <div className="flex min-h-0 flex-1">
        <div
          className={cx(
            'flex w-11 shrink-0 flex-col justify-between pr-2 text-right text-[11px] leading-none text-muted-foreground tabular-nums',
            heightClass,
          )}
        >
          {ticks.map(t => (
            <span key={t}>{formatTick(t)}</span>
          ))}
        </div>
        <div className={cx('relative flex-1', heightClass)}>
          {ticks.map((t, i) => (
            <div
              key={t}
              className="absolute inset-x-0 border-t border-border"
              style={{ top: `${(i / (ticks.length - 1)) * 100}%` }}
            />
          ))}
          <div className="absolute inset-0 flex items-end gap-1">
            {labels.map((_, barIndex) => {
              // Cumulative offsets from the baseline, counting only the shown segments; the top rounded
              // corner belongs to the last shown segment.
              let cumulative = 0;
              let topShown = -1;
              const segments = series.map((s, seriesIndex) => {
                const value = valueAt(s, barIndex);
                const shown = value > 0;
                const bottom = cumulative;
                if (shown) {
                  cumulative += value;
                  topShown = seriesIndex;
                }
                return { s, seriesIndex, value, shown, bottom };
              });
              return (
                // eslint-disable-next-line react/no-array-index-key -- static mock series, order is stable
                <div key={barIndex} className="relative h-full flex-1">
                  {segments.map(seg => (
                    <Tooltip key={seg.s.key}>
                      <TooltipTrigger asChild>
                        <div
                          className={cx(
                            'absolute inset-x-0 cursor-default transition-all duration-300 ease-out',
                            seg.seriesIndex === topShown && 'rounded-t',
                          )}
                          style={{
                            bottom: `${(seg.bottom / niceMax) * 100}%`,
                            height: `max(0px, calc(${(seg.value / niceMax) * 100}% - 2px))`,
                            backgroundColor: seg.s.color,
                            opacity: seg.shown ? 1 : 0,
                            pointerEvents: seg.shown ? undefined : 'none',
                          }}
                        />
                      </TooltipTrigger>
                      {seg.shown && <TooltipContent>{tip(seg.seriesIndex, barIndex, seg.value)}</TooltipContent>}
                    </Tooltip>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="mt-1 flex gap-1 pl-11 text-[10px] leading-none text-muted-foreground">
        {labels.map((l, i) => (
          // eslint-disable-next-line react/no-array-index-key -- static mock series, order is stable
          <span key={i} className="flex-1 whitespace-nowrap text-center">
            {l}
          </span>
        ))}
      </div>
    </div>
  );
};

// Horizontal stacked bar showing a breakdown by severity, with a labelled legend.
/** The response-time chart in miniature: only its line, coloured by the zone each stretch of it sits in. */
export const ResponseSparkline = ({
  series,
  fastBelow,
  slowAtOrAbove,
  className,
}: {
  series: TrendPoint[];
  fastBelow: number;
  slowAtOrAbove: number;
  className?: string;
}): JSX.Element | null => {
  const uid = React.useId().replace(/:/g, '');
  const n = series.length;
  if (n < 2) return null;
  const values = series.map(p => p.value);
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  // Room above and below, so the stroke is not clipped at the edge.
  const pad = (hi - lo || hi * 0.2 || 1) * 0.15;
  const min = lo - pad;
  const span = hi + pad - min;
  // Inset from both sides, so the line sits inside its panel rather than running edge to edge.
  const INSET = 8;
  const x = (i: number): number => INSET + (i / (n - 1)) * (100 - 2 * INSET);
  const y = (v: number): number => (1 - (v - min) / span) * 100;
  const clampY = (v: number): number => Math.min(100, Math.max(0, y(v)));
  const line = smoothPath(series.map((p, i) => [x(i), y(p.value)]));

  // Keyed to the value axis, so a stretch above a threshold takes that zone's colour.
  const gradientId = `sparkZones${uid}`;
  const slowY = clampY(slowAtOrAbove);
  const fastY = clampY(fastBelow);
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className={className} aria-hidden>
      <defs>
        <linearGradient id={gradientId} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2="100">
          <stop offset="0%" stopColor={STATUS_META.risk.stroke} />
          <stop offset={`${slowY}%`} stopColor={STATUS_META.risk.stroke} />
          <stop offset={`${slowY}%`} stopColor={STATUS_META.watch.stroke} />
          <stop offset={`${fastY}%`} stopColor={STATUS_META.watch.stroke} />
          <stop offset={`${fastY}%`} stopColor={STATUS_META.good.stroke} />
          <stop offset="100%" stopColor={STATUS_META.good.stroke} />
        </linearGradient>
      </defs>
      <path
        d={line}
        fill="none"
        stroke={`url(#${gradientId})`}
        strokeWidth={2}
        vectorEffect="non-scaling-stroke"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
};

// Multi-segment donut (e.g. repos split by activity), with the total in the centre.
export const Donut = ({
  segments,
  centerLabel,
  className,
}: {
  segments: { label: string; value: number; color: string }[];
  centerLabel?: string;
  className?: string;
}): JSX.Element => {
  const r = 38;
  const circ = 2 * Math.PI * r;
  const total = segments.reduce((sum, s) => sum + s.value, 0) || 1;
  let offset = 0;
  return (
    <div className={cx('relative inline-flex items-center justify-center', className)}>
      <svg width={100} height={100} viewBox="0 0 100 100">
        <circle cx={50} cy={50} r={r} fill="none" strokeWidth={9} style={{ stroke: 'var(--border)' }} />
        {segments
          .filter(s => s.value > 0)
          .map(s => {
            const len = (s.value / total) * circ;
            const dash = (
              <circle
                key={s.label}
                cx={50}
                cy={50}
                r={r}
                fill="none"
                strokeWidth={9}
                style={{ stroke: s.color, strokeDasharray: `${len} ${circ - len}`, strokeDashoffset: -offset }}
                transform="rotate(-90 50 50)"
              />
            );
            offset += len;
            return dash;
          })}
      </svg>
      <div className="absolute flex flex-col items-center leading-none">
        <span className="text-xl font-bold text-foreground">{total}</span>
        {centerLabel && <span className="mt-1 text-[10px] text-muted-foreground">{centerLabel}</span>}
      </div>
    </div>
  );
};

// The value of one health metric at its two anchors (handoff / project start, and today), grouped
// close together so the before/after reads at a glance.
export const AnchorTrend = ({ trend, className }: { trend: TrendPoint[]; className?: string }): JSX.Element => (
  <div className={cx('flex items-center gap-5', className)}>
    {trend.map((point, i) => (
      <React.Fragment key={point.label}>
        {i > 0 && <ChevronRight className="size-3 shrink-0 text-muted-foreground/40" aria-hidden />}
        <div className="flex flex-col items-center text-center">
          <span className={cx('text-sm font-semibold tabular-nums', STATUS_META[point.status].text)}>
            {point.display}
          </span>
          <span className="mt-0.5 text-[10px] leading-tight text-muted-foreground">{point.label}</span>
        </div>
      </React.Fragment>
    ))}
  </div>
);

export interface ShareLine {
  key: string;
  label: string;
  /** Percent per point, null where there is nothing to measure; the line joins across it. */
  values: (number | null)[];
  color: string;
  dashed?: boolean;
}

const SHARE_TICKS = [100, 75, 50, 25, 0];

/** Percent lines over time on a fixed 0 to 100 axis, with shaded bands marking the periods they cross. */
export const ShareLinesChart = ({
  lines,
  bands,
  xLabels,
  axis,
  className,
}: {
  lines: ShareLine[];
  /** Inclusive point indexes; a band runs to the next point, or to the edge when it is the last. */
  bands: { from: number; to: number; label: string }[];
  /** Only the points worth labelling, by index. */
  xLabels: { index: number; label: string }[];
  axis: string;
  className?: string;
}): JSX.Element => {
  const count = Math.max(...lines.map(line => line.values.length), 1);
  const x = (i: number): number => (count > 1 ? (i / (count - 1)) * 100 : 50);
  const y = (share: number): number => 100 - share;
  const points = (values: (number | null)[]): string =>
    values.flatMap((v, i) => (v === null ? [] : [`${x(i).toFixed(2)},${y(v).toFixed(2)}`])).join(' ');

  return (
    <div className={cx('flex min-w-0 flex-col gap-2', className)}>
      <p className="text-[11px] text-muted-foreground">{axis}</p>
      <div className="flex h-48">
        <div className="relative mr-1.5 h-full w-9 shrink-0">
          {SHARE_TICKS.map(t => (
            <span
              key={t}
              className="absolute right-0 -translate-y-1/2 text-[10px] leading-none text-muted-foreground tabular-nums"
              style={{ top: `${y(t)}%` }}
            >
              {t}%
            </span>
          ))}
        </div>
        <div className="relative h-full min-w-0 flex-1 border-b border-l border-border">
          {bands.map(band => {
            const left = x(band.from);
            const right = band.to + 1 < count ? x(band.to + 1) : 100;
            return (
              <div
                key={band.label}
                title={band.label}
                className="@container absolute inset-y-0 overflow-hidden border-l border-dashed border-border bg-muted/60"
                style={{ left: `${left}%`, width: `${right - left}%` }}
              >
                {/* Too narrow for a few letters, the label is dropped and the band's title names it. */}
                <span className="absolute inset-x-1.5 top-1 hidden truncate text-[10px] font-medium uppercase tracking-wide text-muted-foreground @min-[3.5rem]:block">
                  {band.label}
                </span>
              </div>
            );
          })}
          {SHARE_TICKS.map(t => (
            <div key={t} className="absolute inset-x-0 border-t border-border/60" style={{ top: `${y(t)}%` }} />
          ))}
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
            {lines.map(line => (
              <polyline
                key={line.key}
                points={points(line.values)}
                fill="none"
                // Through style: globals.css forces `svg [stroke]` to currentColor.
                style={{ stroke: line.color }}
                strokeWidth={line.dashed ? 1.6 : 2.8}
                strokeDasharray={line.dashed ? '5 3' : undefined}
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            ))}
          </svg>
        </div>
      </div>
      <div className="relative ml-[42px] h-3 text-[10px] leading-none text-muted-foreground">
        {xLabels.map(({ index, label }) => (
          <span key={index} className="absolute -translate-x-1/2 whitespace-nowrap" style={{ left: `${x(index)}%` }}>
            {label}
          </span>
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {lines.map(line => (
          <span key={line.key} className="inline-flex items-center gap-1.5">
            <span className="h-0.5 w-4" style={{ backgroundColor: line.color, opacity: line.dashed ? 0.7 : 1 }} />
            {line.label}
          </span>
        ))}
      </div>
    </div>
  );
};
